// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * The key-shape rule registry — THE FALLBACK TIER, and the UI says so on screen.
 *
 * leakmap's primary mechanism is exact search for the user's own values. These 12 families exist for
 * the case where no `.env` was supplied, or where a credential was hardcoded and never went through
 * `.env` at all. The registry is deliberately capped at 12: a general-purpose recogniser sprawl is
 * `scrubjay`'s product, it is better than a copy of it would be, and measured on a real Vite build
 * its 29 rules covered 50–56% of what shipped with two junk hits (an `email-address` rule firing on
 * a Sentry DSN, a `credit-card` rule firing on a 17-digit run inside an Airtable token).
 *
 * THE TIER COLUMN IS THE IMPORTANT ONE. A Firebase `AIza…` key in a client bundle is EXPECTED and
 * Google says so in as many words: "API keys restricted to Firebase services do not need to be
 * treated as secrets, and it's safe to include them in your code or configuration files". Flagging
 * that as a five-alarm fire would destroy the tool's credibility on first contact with a real app.
 */

export type Tier = 'SECRET' | 'PUBLIC' | 'CONTEXT';

export interface ShapeRule {
  readonly id: string;
  readonly label: string;
  readonly re: RegExp;
  readonly tier: Tier;
  readonly rotateUrl?: string;
  readonly note?: string;
}

/**
 * `sk-…T3BlbkFJ…` — the infix is not a magic string, it is literally base64 of the vendor name:
 * `atob("T3BlbkFJ") === "OpenAI"`. A free, offline discriminator that makes the rule far tighter
 * than a bare `{100,140}` length class.
 */
export const SHAPE_RULES: readonly ShapeRule[] = [
  {
    id: 'openai',
    label: 'OpenAI API key',
    re: /\bsk-(?:proj|svcacct|admin)-(?:[A-Za-z0-9_-]{58}|[A-Za-z0-9_-]{74})T3BlbkFJ(?:[A-Za-z0-9_-]{58}|[A-Za-z0-9_-]{74})\b|\bsk-[a-zA-Z0-9]{20}T3BlbkFJ[a-zA-Z0-9]{20}\b/g,
    tier: 'SECRET',
    rotateUrl: 'https://platform.openai.com/api-keys',
  },
  {
    id: 'anthropic',
    label: 'Anthropic API key',
    re: /\bsk-ant-(?:api03|admin01)-[a-zA-Z0-9_-]{93}AA\b/g,
    tier: 'SECRET',
    // The new host, not the console redirect.
    rotateUrl: 'https://platform.claude.com/settings/keys',
  },
  {
    id: 'google-aiza',
    label: 'Google API key',
    re: /\bAIza[\w-]{35}\b/g,
    tier: 'CONTEXT',
    note: 'Which Google service this key is for cannot be determined from the key itself.',
  },
  {
    id: 'huggingface',
    label: 'Hugging Face token',
    re: /\bhf_[a-zA-Z]{34}\b/g,
    tier: 'SECRET',
    rotateUrl: 'https://huggingface.co/settings/tokens',
  },
  {
    id: 'stripe-secret',
    label: 'Stripe secret key',
    re: /\b(?:sk|rk)_(?:live|test|prod)_[A-Za-z0-9]{10,99}\b/g,
    tier: 'SECRET',
    rotateUrl: 'https://dashboard.stripe.com/apikeys',
    note: 'A sk_test_ key still reads your sandbox customer objects. Rank it low; never ignore it.',
  },
  {
    id: 'stripe-publishable',
    label: 'Stripe publishable key',
    re: /\bpk_(?:live|test)_[A-Za-z0-9]{10,99}\b/g,
    tier: 'PUBLIC',
    note: 'Publishable keys are designed to be in client code.',
  },
  {
    id: 'github',
    label: 'GitHub token',
    re: /\bgh[pousr]_[0-9a-zA-Z]{36}\b|\bgithub_pat_\w{82}\b/g,
    tier: 'SECRET',
    rotateUrl: 'https://github.com/settings/tokens',
  },
  {
    id: 'aws-akid',
    // `[A-Z0-9]` is WRONG for the body: current AWS tokens cannot contain 0, 1, 8 or 9. And `A3T` is
    // a real prefix that the common rule omits.
    label: 'AWS access key ID',
    re: /\b(?:A3T[A-Z0-9]|AKIA|ASIA|ABIA|ACCA)[A-Z2-7]{16}\b/g,
    tier: 'SECRET',
    rotateUrl: 'https://console.aws.amazon.com/iam/',
    note: 'An ASIA… key is a temporary session credential and expires on its own.',
  },
  {
    id: 'supabase-secret',
    label: 'Supabase secret key',
    re: /\bsb_secret_[A-Za-z0-9_-]{20,}\b/g,
    tier: 'SECRET',
    rotateUrl: 'https://supabase.com/dashboard/project/_/settings/api-keys',
    note: 'Supabase: "You cannot use a secret key in the browser … it will always reply with HTTP 401 Unauthorized." Create the new key, deploy, then delete the old one — deleting a secret key is irreversible.',
  },
  {
    id: 'supabase-publishable',
    label: 'Supabase publishable key',
    re: /\bsb_publishable_[A-Za-z0-9_-]{20,}\b/g,
    tier: 'PUBLIC',
  },
  {
    id: 'slack',
    label: 'Slack token or webhook',
    re: /\bxoxb-[0-9]{10,13}-[0-9]{10,13}[a-zA-Z0-9-]*\b|\bxox[pe](?:-[0-9]{10,13}){3}-[a-zA-Z0-9-]{28,34}\b|\bxapp-\d-[A-Za-z0-9-]{10,}\b|https:\/\/hooks\.slack\.com\/(?:services|workflows|triggers)\/[A-Za-z0-9/]+/g,
    tier: 'SECRET',
    rotateUrl: 'https://api.slack.com/apps',
  },
  {
    id: 'pem-private-key',
    label: 'Private key block',
    re: /-----BEGIN (?:RSA |EC |DSA |OPENSSH |PGP )?PRIVATE KEY-----/g,
    tier: 'SECRET',
  },
  {
    id: 'credential-url',
    label: 'Connection string with credentials',
    re: /\b(?:postgres(?:ql)?|mysql|mongodb(?:\+srv)?|redis|rediss|amqp|amqps):\/\/[^:@\s"'`]+:[^@\s"'`]+@[^\s"'`]+/g,
    tier: 'SECRET',
    note: 'The most damaging single string that can land in a client bundle.',
  },
];

/**
 * The `aws-sak` rule is deliberately NOT in the table above: a bare `[A-Za-z0-9/+=]{40}` matches an
 * enormous amount of ordinary minified content. It fires only by CO-OCCURRENCE — either the literal
 * came from the user's `.env`, or it sits within 512 bytes of an access-key ID.
 */
export const AWS_SECRET_RE = /\b[A-Za-z0-9/+=]{40}\b/g;
export const AWS_SAK_PROXIMITY = 512;

/**
 * Three call-site checks. Cheap, and genuinely diagnostic: `dangerouslyAllowBrowser` is the OpenAI
 * SDK flag you have to set on purpose to use it client-side, and a client-side reference to a
 * provider's API host means a key is being used from the browser whether or not we found it.
 */
export const CALL_SITE_CHECKS: readonly { id: string; needle: string; title: string; detail: string }[] = [
  {
    id: 'dangerously-allow-browser',
    needle: 'dangerouslyAllowBrowser',
    title: 'The OpenAI SDK is being used directly from the browser',
    detail:
      'dangerouslyAllowBrowser is the flag the SDK makes you set to run in a browser, precisely because doing so means your API key is in the client. Move these calls behind a server route or an edge function.',
  },
  {
    id: 'client-openai',
    needle: 'api.openai.com',
    title: 'Your client code calls api.openai.com directly',
    detail: 'A browser cannot call this endpoint without a key that the browser also has.',
  },
  {
    id: 'client-anthropic',
    needle: 'api.anthropic.com',
    title: 'Your client code calls api.anthropic.com directly',
    detail: 'A browser cannot call this endpoint without a key that the browser also has.',
  },
  {
    id: 'client-gemini',
    needle: 'generativelanguage.googleapis.com',
    title: 'Your client code calls the Gemini API directly',
    detail:
      'Google, on this exact case: "The API key that you use for calls to the Gemini Developer API in your app should never be included in your code or configuration files. It must be protected from public exposure."',
  },
];

/**
 * `AIza…` CLASSIFICATION IS BY NEIGHBOURHOOD, NOT BY SHAPE. A Firebase web key, a Maps JS key and a
 * Gemini Developer key are all `AIza` + 35 characters and are indistinguishable from the string
 * alone. The consequence of getting it wrong runs in both directions: calling a Firebase key a leak
 * is crying wolf, and calling a Gemini key expected is a real, expensive miss.
 */
export function classifyGoogleKey(context: string): { tier: Tier; label: string; detail: string } {
  if (/authDomain|messagingSenderId|appId|firebaseConfig|storageBucket|firebaseapp\.com/.test(context)) {
    return {
      tier: 'PUBLIC',
      label: 'Firebase Web API key',
      detail:
        'Google: "API keys restricted to Firebase services do not need to be treated as secrets, and it\'s safe to include them in your code or configuration files." They are not used to control access to backend resources — your Security Rules are.',
    };
  }
  if (/generativelanguage\.googleapis\.com|GEMINI_API_KEY|@google\/genai|GoogleGenerativeAI/.test(context)) {
    return {
      tier: 'SECRET',
      label: 'Gemini Developer API key',
      detail:
        'Google: "The API key that you use for calls to the Gemini Developer API in your app should never be included in your code or configuration files. It must be protected from public exposure."',
    };
  }
  if (/maps\.googleapis\.com|google\.maps|libraries=places/.test(context)) {
    return {
      tier: 'PUBLIC',
      label: 'Google Maps JavaScript API key',
      detail:
        'Maps JS keys are meant to be in client code. Check that an HTTP-referrer restriction is set on it, or anyone can spend your quota.',
    };
  }
  return {
    tier: 'CONTEXT',
    label: 'Google API key, service unknown',
    detail: "leakmap cannot tell which Google API this key is for from the key alone, and the surrounding code does not say.",
  };
}

/**
 * The placeholder gate — the #1 real-world false positive is a README snippet.
 *
 * NO CHECKSUM GATE. GitHub documents the mechanism ("A 32 bit checksum in the last 6 digits of each
 * token … a CRC32 algorithm … encoded with a Base62 implementation") but not the covered byte range
 * or the alphabet. Shipping an unverified checksum silently drops TRUE positives, which is the one
 * failure mode this tool cannot accept.
 */
const EXAMPLES = new Set([
  'AKIAIOSFODNN7EXAMPLE',
  'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY',
]);

/**
 * The sentence that goes on every shape finding, because the honesty gate forbids "this key is
 * live". Validating a key would mean transmitting the user's secret to a third party — the exact
 * thing this tool exists to detect — so leakmap reports a shape and refuses to report a status.
 */
export const PLACEHOLDER_NOTE =
  'leakmap recognises the SHAPE of this credential; it has not tested it against any provider and does not know whether it still works.';

export function isPlaceholder(candidate: string): boolean {
  if (EXAMPLES.has(candidate)) return true;
  if (/EXAMPLE$/.test(candidate)) return true;
  if (new Set(candidate).size <= 4) return true;
  if (/(.)\1{7,}/.test(candidate)) return true;
  return false;
}

export interface ShapeHit {
  readonly ruleId: string;
  readonly label: string;
  readonly tier: Tier;
  readonly index: number;
  readonly length: number;
  readonly value: string;
  readonly rotateUrl?: string;
  readonly detail?: string;
}

/** Run the registry over one decoded text asset. */
export function findShapes(text: string): ShapeHit[] {
  const out: ShapeHit[] = [];
  for (const rule of SHAPE_RULES) {
    rule.re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = rule.re.exec(text)) !== null) {
      const value = m[0];
      if (m[0].length === 0) {
        rule.re.lastIndex++;
        continue;
      }
      if (isPlaceholder(value)) continue;

      if (rule.id === 'google-aiza') {
        const ctx = text.slice(Math.max(0, m.index - 500), m.index + 500);
        const g = classifyGoogleKey(ctx);
        out.push({
          ruleId: rule.id,
          label: g.label,
          tier: g.tier,
          index: m.index,
          length: value.length,
          value,
          detail: g.detail,
        });
        continue;
      }
      out.push({
        ruleId: rule.id,
        label: rule.label,
        tier: rule.tier,
        index: m.index,
        length: value.length,
        value,
        rotateUrl: rule.rotateUrl,
        detail: rule.note,
      });
    }
  }
  return out;
}

/**
 * The no-`.env` tier that actually works, and it is free.
 *
 * Measured: when source reads `import.meta.env` as a WHOLE OBJECT rather than per key, Vite injects
 * the entire env object WITH THE KEY NAMES INTACT:
 *
 *     const A={VITE_ADMIN_PASSWORD:"Tr0ub4dor&3-correct-horse",VITE_ANTHROPIC_KEY:"sk-ant-api03-…"}
 *
 * Per-key access emits values only. This is a far better no-`.env` tier than twelve prefix rules,
 * because it reports leaks BY VARIABLE NAME with no `.env` supplied at all.
 */
const INJECTED_OBJECT_RE = /\{\s*((?:[A-Z][A-Z0-9_]{2,}\s*:\s*(?:"[^"]*"|'[^']*'|`[^`]*`)\s*,?\s*){2,})\}/g;
const INJECTED_PAIR_RE = /([A-Z][A-Z0-9_]{2,})\s*:\s*(?:"([^"]*)"|'([^']*)'|`([^`]*)`)/g;

export interface InjectedEnvHit {
  readonly key: string;
  readonly valueLength: number;
  readonly index: number;
  readonly value: string;
}

export function findInjectedEnvObjects(text: string, keyLooksRelevant: (k: string) => boolean): InjectedEnvHit[] {
  const out: InjectedEnvHit[] = [];
  INJECTED_OBJECT_RE.lastIndex = 0;
  let obj: RegExpExecArray | null;
  while ((obj = INJECTED_OBJECT_RE.exec(text)) !== null) {
    const body = obj[1]!;
    const pairs: InjectedEnvHit[] = [];
    let relevant = 0;
    INJECTED_PAIR_RE.lastIndex = 0;
    let p: RegExpExecArray | null;
    while ((p = INJECTED_PAIR_RE.exec(body)) !== null) {
      const key = p[1]!;
      const value = p[2] ?? p[3] ?? p[4] ?? '';
      if (keyLooksRelevant(key)) relevant++;
      pairs.push({ key, value, valueLength: value.length, index: obj.index + p.index });
    }
    // At least two recognisable env-shaped keys, or this is just an ordinary constant object.
    if (relevant >= 2) out.push(...pairs.filter((x) => keyLooksRelevant(x.key)));
  }
  return out;
}
